import { html } from 'lit';
import { ViewBase } from '../view-base.js';
import { homeOnboardViewStyles } from './home-onboard-view-styles.js';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/color-picker/color-picker.js';

import { Q1SpinnerPanel } from '../../components/q1-spinner-panel/q1-spinner-panel.js';
import { Q1PlacesInput } from '../../components/q1-places-input/q1-places-input.js';
import { Q1TutorialList } from '../../components/q1-tutorial-list/q1-tutorial-list.js';

import { q1OnboardingFunctions } from '../../modules/q1-core-functions.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';
import { updateMemberById } from '../../modules/firestore-helpers.js';

const TAG_GROUP = 'external-tools'; // which group is required for onboarding

const JOIN_SLACK_LINK =
  'https://join.slack.com/t/q1-is/shared_invite/zt-2xrcbblwj-3SvWUFFNk_NwlUo9KFB8Ug';

export class HomeOnboardView extends FormHostMixin(ViewBase) {
  static styles = [
    ...super.styles,
    homeOnboardViewStyles,
  ];

  static properties = {
    ...super.properties,
    
    loading: { type: Boolean },

    tutorialManager: { type: Object },
    requiredTutorials: { type: Array },
    requiredTutorialCount: { type: Number },
    requiredTutorialCompletedCount: { type: Number },
    allRequiredTutorialsCompleted: { type: Boolean },
    
    profile_newUploadPath: { type: String },

    headline: { type: String },
    colorPrimary: { type: String },
    locationsConfig: { type: Object },
  };

  constructor() {
    super();

    this.loading = true;

    this.tutorialManager = null;
    this.requiredTutorials = [];
    this.requiredTutorialCount = 0;
    this.requiredTutorialCompletedCount = 0;
    this.allRequiredTutorialsCompleted = false;

    this.profile_newUploadPath = '';

    this.headline = '';
    this.colorPrimary = '';
    this.locationsConfig = [];
  }
  
  connectedCallback() {
    super.connectedCallback();

    this.setViewMeta();

    this.tutorialManager = this.appElement.tutorialManager;
    this._initView();
  }

  _initView() {
    const cm = this.currentMember || {};

    this.headline = cm.headline || '';
    this.colorPrimary = cm.colorPrimary || '';
    this.locationsConfig = cm.locationsConfig || [];

    this.tutorialManager.loadingComplete.then(() => {
      this._rebuildRequiredTutorials();
      this.loading = false;
    }).catch((error) => {
      console.log('Error loading tutorials:', error);
      this.loading = false;
    });
  }

  _rebuildRequiredTutorials() {
    const tagGroup = this.tutorialManager.getTutorialsByTag(TAG_GROUP);

    this.requiredTutorials = tagGroup.tutorials;
    this.requiredTutorialCount = tagGroup.tutorialCount;
    this.requiredTutorialCompletedCount = tagGroup.completedCount;
    this.allRequiredTutorialsCompleted = tagGroup.allCompleted;
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('currentMember')) {
      const colorPrimary = this.colorPrimary;
      const headline = this.headline;

      this._initView();

      this.colorPrimary = colorPrimary;
      this.headline = headline;
    }
  }

  handleTrackView(event) {
    const tutorialKey = event.detail.tutorialKey;

    this.tutorialManager.trackTutorialView(tutorialKey);
  }

  handleMarkCompleted(event) {
    const tutorialKey = event.detail.tutorialKey;

    const colorPrimary = this.colorPrimary;
    const headline = this.headline;

    this.tutorialManager
      .markTutorialCompleted(tutorialKey).then(() => {
        // Tutorial Manager has already updated the tutorial, we just need to re-fire the lit 
        // setter so that the q1-tutorials-list is refreshed.
        this._rebuildRequiredTutorials();
        this.colorPrimary = colorPrimary;
        this.headline = headline;

        this.appElement.refreshCurrentMember(); // update the app header
        
        this.showToast('Tutorial marked as completed.', null, 'success');
      }).catch((error) => {
        console.log('Error marking tutorial complete: ', error);
        this.showToast(error.message, 'Error Marking Tutorial Completed.', 'danger');
        this.loading = false;
      });
  }

  handleLocationsChange() {
    const locationsInput = this.renderRoot.querySelector('#locations-input');
    this.locationsConfig = locationsInput.places;
  }

  async completeOnboarding() {
    this.loading = true;
    window.scrollTo({ top: 0 });

    // Error State 1: Tutorials not completed
    if (!this.allRequiredTutorialsCompleted) {
      this.showToast(
        `So far you've completed ${this.requiredTutorialCompletedCount} `
          + `of ${this.requiredTutorialCount}.`,
        'Please complete the required tutorials first.',
        'danger'
      );
      this.loading = false;
      return;
    }

    const profileForm = this.renderRoot.querySelector('#profile-form');
    
    // Error State 2: Form invalid
    if (!profileForm.reportValidity()) {
      this.showToast('Please fix the form validation errors to continue', null, 'danger');
      this.loading = false;
      return;
    }

    try {
      const profileFormData = {
        ...this._serializeForm(profileForm),
        locationsConfig: this.locationsConfig,
      };
      console.log('profileFormData', profileFormData);

      await updateMemberById(this.currentMember.id, profileFormData);
    } catch (error) {
      console.log('Error updating member:', error);
      this.showToast(error.message, 'Error updating your member record', 'danger');
      this.loading = false;
      return;
    }
  
    const { completeOnboarding } = q1OnboardingFunctions;
  
    try {
      await completeOnboarding({});
      this.appElement.queueToast(
        'Congratulations and welcome to the Quorum.',
        'Onboarding complete!',
        'success'
      );
      window.location.href = '/home'; // purposely trigger full page reload
    } catch (error) {
      console.log('Error completing onboarding:', error);
      this.showToast(error.message, 'Error completing your onboarding', 'danger', 10000);
      this.loading = false;
    }
  }

  get contentTemplate() {
    return html`
      <h1>New Member Onboarding</h1>

      <p class="subtitle">
        To finish activating your new membership, please complete the items below.
      </p>

      <hr/>

      <content-container>
        <tutorial-panel>
          ${this.tutorialPanelTemplate}
          ${this.slackJoinTemplate}
        </tutorial-panel>

        <profile-panel>
          ${this.profilePanelTemplate}
        </profile-panel>
      </content-container>

      <hr/>

      <button-bar>
        <sl-button
          id="complete-onboarding"
          @click="${this.completeOnboarding}"
          ?disabled="${this.loading}"
          variant="primary"
          size="large"
        >
          Complete Onboarding
          <sl-icon library="material" name="check_circle" slot="suffix"></sl-icon>
        </sl-button>
      </button-bar>

      <q1-spinner-panel label="Saving..." ?open=${this.loading}></q1-spinner-panel>
    `;
  }

  get profilePanelTemplate() {
    return html`
      <h2>Your Member Profile</h2>

      <p>
        This is how other members will see you.
      </p>

      <form id="profile-form" class="q1">

        <field-pair>
          <q1-image-upload-input
            id="profile_newUploadPath"
            name="profile_newUploadPath"
            upload-folder="member-profile"
            current-image-thumbnail=${this.cmImage_profile?.mini}
            @change="${this.handleInput}"
            label="Profile Image"
            required
          ></q1-image-upload-input>

          <input-wrapper>
            <label for="colorPrimary">Profile Color</label>
            <sl-color-picker
              id="colorPrimary"
              name="colorPrimary"
              .value=${this.colorPrimary}
              label="Profile Color"
              @sl-change="${this.handleInput}"
              size="large"
              swatches="#D81D39; #DB3A1A; #DE6616; #E2B013; #9DBC38; #53A84D; 
                #4AAB99; #488BAD; #4864AD; #7D52A3; #A74E9A; #C62F81;"
            ></sl-color-picker>
          </input-wrapper>
        </field-pair>

        <field-pair>
          <sl-textarea 
            id="headline"
            name="headline"
            .value="${this.headline}"
            @sl-input="${this.handleInput}"
            label="Profile Headline"
            rows="3"
            maxlength="250"
            required
          >
            <span slot="help-text">
              Shown under your name on your profile.
            </span>
          </sl-textarea>

          <q1-places-input
            id="locations-input"
            label="Locations"
            help-text="Add all the places you've lived, worked, or built a significant network"
            .places=${this.locationsConfig}
            @change=${this.handleLocationsChange}
            required
          ></q1-places-input>
        </field-pair>

      </form>
    `;
  }

  get tutorialPanelTemplate() {
    return html`
      <h2>Onboarding Tutorials</h2>

      <p>
        The tutorials below are required for all new members.
      </p>
      
      <q1-tutorial-list
        .tutorials=${this.requiredTutorials}
        @track-view=${this.handleTrackView}
        @mark-completed=${this.handleMarkCompleted}
      ></q1-tutorial-list>
    `;
  }

  get slackJoinTemplate() {
    return html`
      <h2>Slack</h2>
      <p>
        The q1 Network Slack is currently the best way to stay abreast of updates from the projects 
        & guilds you care about. We host several community channels for stimulating  
        <a href='https://quorum1.enterprise.slack.com/archives/C08UREBRFFA' 
        target=_blank>#q1-connections</a> to amplify impact within the network and beyond.
      </p>
      <p>
      The network Slack offers chat functionality, robust workflow automation, deep integration with 
      other tools, and acts as a hub for operational coordination for projects & guilds on q1.is             
      </p>
      <button-bar>
        <sl-button
          variant="warning"
          size="large"
          href=${JOIN_SLACK_LINK}
          target="_blank"
        >
          <sl-icon slot="prefix" library="tabler" name="brand-slack"></sl-icon>
          Join q1 Network Slack
        </sl-button>
      </button-bar>
    `;
  }

}

customElements.define('home-onboard-view', HomeOnboardView);
